/**
 * Système de permissions basé sur les rôles (RBAC)
 * 
 * Les permissions sont définies par le backend et renvoyées dans le champ `permissions[]`
 * de l'objet User retourné par /auth/me
 * 
 * Documentation: API DOCUMENTATION/users.md - Section "Obtenir les Permissions Disponibles"
 */

import type { UserRole } from './auth';

/**
 * Permissions disponibles dans le système (définies par le backend)
 * Source: GET /users/permissions
 */
export type Permission =
  // === JOURNAUX ===
  | 'view_journals'       // Consulter les journaux
  | 'create_entries'      // Créer des écritures
  | 'validate_entries'    // Valider des écritures
  | 'delete_entries'      // Supprimer des écritures
  
  // === COMPTES ===
  | 'view_accounts'       // Consulter le plan comptable
  | 'manage_accounts'     // Gérer les comptes
  
  // === RAPPORTS ===
  | 'view_reports'        // Consulter les rapports
  | 'generate_reports'    // Générer des rapports
  
  // === DONNÉES ===
  | 'export_data'         // Exporter les données
  | 'import_data'         // Importer des données
  
  // === ADMINISTRATION ===
  | 'manage_users'        // Gérer les utilisateurs
  | 'manage_settings'     // Gérer les paramètres
  | 'manage_integrations' // Gérer les intégrations
  
  // === EXERCICES FISCAUX ===
  | 'view_fiscal_years'   // Consulter les exercices
  | 'manage_fiscal_years' // Gérer les exercices (créer, clôturer, réouvrir)
  
  // === IMMOBILISATIONS ===
  | 'manage_fixed_assets'  // Gérer les immobilisations et amortissements
  
  // === COMPTABILITÉ ANALYTIQUE ===
  | 'manage_analytic_accounts' // Gérer les comptes analytiques et ventilations
  
  // === ÉCRITURES RÉCURRENTES ===
  | 'manage_recurring_entries' // Gérer les écritures récurrentes
  
  // === LETTRAGE ===
  | 'manage_reconciliation'   // Lettrer/dé-lettrer les écritures
  
  // === AUDIT (réservé aux auditeurs) ===
  | 'perform_audit'       // Effectuer un audit d'exercice
;

/**
 * Labels pour les permissions (pour l'affichage UI)
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  'view_journals': 'Consulter les journaux',
  'create_entries': 'Créer des écritures',
  'validate_entries': 'Valider des écritures',
  'delete_entries': 'Supprimer des écritures',
  'view_accounts': 'Consulter le plan comptable',
  'manage_accounts': 'Gérer les comptes',
  'view_reports': 'Consulter les rapports',
  'generate_reports': 'Générer des rapports',
  'export_data': 'Exporter les données',
  'import_data': 'Importer des données',
  'manage_users': 'Gérer les utilisateurs',
  'manage_settings': 'Gérer les paramètres',
  'manage_integrations': 'Gérer les intégrations',
  'view_fiscal_years': 'Consulter les exercices',
  'manage_fiscal_years': 'Gérer les exercices',
  'manage_fixed_assets': 'Gérer les immobilisations',
  'manage_analytic_accounts': 'Gérer la comptabilité analytique',
  'manage_recurring_entries': 'Gérer les écritures récurrentes',
  'manage_reconciliation': 'Lettrer/Dé-lettrer les écritures',
  'perform_audit': 'Effectuer un audit',
};

/**
 * Catégories de permissions
 */
export const PERMISSION_CATEGORIES: Record<string, Permission[]> = {
  journals: ['view_journals', 'create_entries', 'validate_entries', 'delete_entries'],
  accounts: ['view_accounts', 'manage_accounts'],
  reports: ['view_reports', 'generate_reports'],
  data: ['export_data', 'import_data'],
  administration: ['manage_users', 'manage_settings', 'manage_integrations'],
  fiscal_years: ['view_fiscal_years', 'manage_fiscal_years'],
  fixed_assets: ['manage_fixed_assets'],
  analytic: ['manage_analytic_accounts'],
  recurring: ['manage_recurring_entries'],
  reconciliation: ['manage_reconciliation'],
  audit: ['perform_audit'],
};

/**
 * Permissions par défaut selon le rôle (fallback si l'API ne renvoie pas de permissions)
 * 
 * Ces permissions sont utilisées UNIQUEMENT si user.permissions est vide ou undefined.
 * Le backend est la source de vérité pour les permissions.
 * 
 * Rôles alignés avec le backend:
 * - super_admin, admin: Rôles système (non créables via API)
 * - accountant, manager, analyst, audit, viewer: Rôles créables via API
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // Super Admin - Accès complet sauf audit
  super_admin: [
    'view_journals', 'create_entries', 'validate_entries', 'delete_entries',
    'view_accounts', 'manage_accounts',
    'view_reports', 'generate_reports',
    'export_data', 'import_data',
    'manage_users', 'manage_settings', 'manage_integrations',
    'view_fiscal_years', 'manage_fiscal_years',
    'manage_fixed_assets', 'manage_analytic_accounts', 'manage_recurring_entries',
    'manage_reconciliation',
  ],
  
  // Admin - Accès complet sauf audit
  admin: [
    'view_journals', 'create_entries', 'validate_entries', 'delete_entries',
    'view_accounts', 'manage_accounts',
    'view_reports', 'generate_reports',
    'export_data', 'import_data',
    'manage_users', 'manage_settings', 'manage_integrations',
    'view_fiscal_years', 'manage_fiscal_years',
    'manage_fixed_assets', 'manage_analytic_accounts', 'manage_recurring_entries',
    'manage_reconciliation',
  ],
  
  // Accountant (Comptable) - Accès comptabilité complet
  accountant: [
    'view_journals', 'create_entries', 'validate_entries',
    'view_accounts', 'manage_accounts',
    'view_reports', 'generate_reports',
    'export_data',
    'view_fiscal_years', 'manage_fiscal_years',
    'manage_fixed_assets', 'manage_analytic_accounts', 'manage_recurring_entries',
    'manage_reconciliation',
  ],
  
  // Manager (Gérant) - Supervision et validation
  manager: [
    'view_journals', 'validate_entries',
    'view_accounts',
    'view_reports', 'generate_reports',
    'export_data',
    'view_fiscal_years',
  ],
  
  // Analyst (Analyste) - Consultation et rapports avancés
  analyst: [
    'view_journals',
    'view_accounts',
    'view_reports', 'generate_reports',
    'export_data',
    'view_fiscal_years',
  ],
  
  // Audit (Auditeur) - Uniquement audit + lecture
  audit: [
    'view_journals',
    'view_accounts',
    'view_reports',
    'export_data',
    'view_fiscal_years',
    'perform_audit', // Seul rôle avec cette permission
  ],

  // Expert-comptable - Mêmes droits que l'auditeur (pas de perform_audit : il certifie, n'audite pas)
  expert_comptable: [
    'view_journals',
    'view_accounts',
    'view_reports', 'generate_reports',
    'export_data',
    'view_fiscal_years',
  ],

  // Viewer (Lecteur) - Accès basique en lecture seule
  viewer: [
    'view_journals',
    'view_accounts',
    'view_reports',
    'view_fiscal_years',
  ],
};

/**
 * Labels pour les rôles (alignés avec le backend)
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Administrateur',
  admin: 'Administrateur',
  accountant: 'Comptable',
  manager: 'Manager',
  analyst: 'Analyste',
  audit: 'Auditeur',
  expert_comptable: 'Expert-comptable',
  viewer: 'Lecteur',
};

/**
 * Descriptions des rôles (alignées avec le backend)
 */
export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  super_admin: 'Accès complet à toutes les fonctionnalités de la plateforme',
  admin: 'Gestion complète de l\'organisation (sauf audit)',
  accountant: 'Gestion comptable et écritures comptables',
  manager: 'Supervision et validation des opérations',
  analyst: 'Analyse financière et génération de rapports',
  audit: 'Audit légal des exercices comptables',
  expert_comptable: 'Supervision, certification et assistance comptable',
  viewer: 'Consultation en lecture seule',
};

/**
 * Vérifie si un rôle a une permission par défaut
 */
export function hasDefaultPermission(role: UserRole, permission: Permission): boolean {
  return DEFAULT_ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Récupère les permissions par défaut d'un rôle
 */
export function getDefaultPermissionsForRole(role: UserRole): Permission[] {
  return DEFAULT_ROLE_PERMISSIONS[role] ?? [];
}
